/**
 * WeatherVisuals.js
 * 
 * Core visual engine for the Living Weather Card.
 * Handles Canvas rendering, particle systems, and performance monitoring.
 * 
 * Features:
 * - 4 Weather Modes: Sunny, Rainy, Snowy, Windy
 * - Performance Optimization: Auto-downgrade on low FPS
 * - Cleanup: Proper resource disposal on unmount
 */


const rnd = (min, max) => Math.random() * (max - min) + min;

// Simplified 2D noise for clouds/textures
const noise2D = (() => {
    const k = new Uint8Array(512);
    for (let i = 0; i < 512; i++) k[i] = (Math.sin(i * 0.1) * 127 + 128) | 0;
    const fade = t => t * t * t * (t * (t * 6 - 15) + 10);
    const lerp = (a, b, t) => (1 - t) * a + t * b;
    function grad(hash, x, y) {
        const h = hash & 15, u = h < 8 ? x : y, v = h < 4 ? y : h === 12 || h === 14 ? x : 0;
        return (h & 1) === 0 ? u + v : u - v;
    }
    return (x, y) => {
        const X = Math.floor(x) & 255, Y = Math.floor(y) & 255;
        x -= Math.floor(x);
        y -= Math.floor(y);
        const u = fade(x), v = fade(y);
        const a = k[X] + Y, aa = k[a], ab = k[a + 1], b = k[X + 1] + Y, ba = k[b], bb = k[b + 1];
        return lerp(lerp(grad(k[aa], x, y), grad(k[ba], x - 1, y), u), lerp(grad(k[ab], x, y - 1), grad(k[bb], x - 1, y - 1), u), v);
    };
})();

export class VisualEngine {
    constructor(canvasRefs, onPerformanceIssue) {
        this.canvases = canvasRefs; // { bg, scene, fx }
        this.ctxs = {};
        this.onPerformanceIssue = onPerformanceIssue;

        // Initialize contexts
        for (let key in this.canvases) {
            if (this.canvases[key]) {
                this.ctxs[key] = this.canvases[key].getContext('2d', { alpha: true });
            }
        }

        this.particles = [];
        this.w = 0;
        this.h = 0;
        this.weather = 'sunny';
        this.time = 0;
        this.mouse = { x: 0.5, y: 0.5 };
        this.lightning = 0;
        this.isRunning = false;
        this.animationId = null;

        // Performance monitoring
        this.frameCount = 0;
        this.lastTime = performance.now();
        this.fpsHistory = [];
    }

    resize(width, height) {
        this.w = width;
        this.h = height;
        const dpr = window.devicePixelRatio || 1;

        for (let key in this.canvases) {
            if (this.canvases[key]) {
                this.canvases[key].width = this.w * dpr;
                this.canvases[key].height = this.h * dpr;
                this.ctxs[key].setTransform(1, 0, 0, 1, 0, 0); // Reset transform
                this.ctxs[key].scale(dpr, dpr);
            }
        }
    }

    setWeather(weather) {
        if (this.weather === weather) return;
        this.weather = weather;
        this.particles = []; // Clear old particles

        // Pre-generate particles based on weather
        if (weather === 'rainy') {
            for (let i = 0; i < 100; i++) this.particles.push(this.createRainDrop());
        } else if (weather === 'snowy') {
            for (let i = 0; i < 80; i++) this.particles.push(this.createSnowFlake());
        } else if (weather === 'windy') {
            for (let i = 0; i < 30; i++) this.particles.push(this.createDebris());
        } else if (weather === 'sunny') {
            for (let i = 0; i < 20; i++) this.particles.push(this.createDust());
        }
    }

    setMouse(x, y) {
        this.mouse.x = x;
        this.mouse.y = y;
    }

    start() {
        if (this.isRunning) return;
        this.isRunning = true;
        this.loop();
    }

    stop() {
        this.isRunning = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
        // Clear canvases
        for (let key in this.ctxs) {
            if (this.ctxs[key]) {
                this.ctxs[key].clearRect(0, 0, this.w, this.h);
            }
        }
        this.particles = [];
    }

    // --- Particle Factories ---
    createRainDrop() {
        return { x: rnd(0, this.w), y: rnd(0, this.h), l: rnd(10, 30), v: rnd(15, 25), a: rnd(0.2, 0.6) };
    }
    createSnowFlake() {
        return { x: rnd(0, this.w), y: rnd(0, this.h), r: rnd(1, 3), v: rnd(0.5, 1.5), sw: rnd(0, Math.PI * 2) };
    }
    createDebris() {
        return { x: rnd(0, this.w), y: rnd(this.h - 200, this.h), r: rnd(2, 5), vx: rnd(3, 8), vy: rnd(-1, 1), rot: rnd(0, 6) };
    }
    createDust() {
        return { x: rnd(0, this.w), y: rnd(0, this.h), r: rnd(0.5, 2), vx: rnd(-0.5, 0.5), vy: rnd(-0.5, 0.5), a: rnd(0.3, 0.7) };
    }

    // --- Main Loop ---
    loop() {
        if (!this.isRunning) return;

        // Performance Check
        const now = performance.now();
        this.frameCount++;
        if (now - this.lastTime >= 1000) {
            const fps = this.frameCount;
            this.fpsHistory.push(fps);
            if (this.fpsHistory.length > 3) this.fpsHistory.shift();

            // If average FPS < 25 for 3 seconds, trigger downgrade
            const avgFps = this.fpsHistory.reduce((a, b) => a + b, 0) / this.fpsHistory.length;
            if (avgFps < 25 && this.onPerformanceIssue) {
                this.onPerformanceIssue();
                // Don't stop immediately, let the component handle it
            }

            this.frameCount = 0;
            this.lastTime = now;
        }

        this.time += 0.016;
        this.update();
        this.draw();

        if (this.weather !== 'sunny') this.lightning *= 0.9;

        this.animationId = requestAnimationFrame(() => this.loop());
    }

    update() {
        const w = this.weather;
        const time = this.time;

        this.particles.forEach(p => {
            if (w === 'rainy') {
                p.y += p.v;
                p.x += (this.mouse.x - 0.5) * 2;
                if (p.y > this.h) { p.y = -p.l; p.x = rnd(0, this.w); }
            } else if (w === 'snowy') {
                p.y += p.v;
                p.x += Math.sin(time + p.sw) * 0.5 + (this.mouse.x - 0.5);
                if (p.y > this.h) { p.y = -5; p.x = rnd(0, this.w); }
            } else if (w === 'windy') {
                p.x += p.vx + (Math.sin(time * 2) * 2);
                p.y += p.vy;
                p.rot += 0.1;
                if (p.x > this.w) p.x = -10;
            } else if (w === 'sunny') {
                p.x += p.vx;
                p.y += p.vy;
                if (p.x < 0 || p.x > this.w) p.vx *= -1;
                if (p.y < 0 || p.y > this.h) p.vy *= -1;
            }
        });
    }

    draw() {
        const { bg: cbg, scene: cs, fx: cfx } = this.ctxs;
        if (!cbg || !cs || !cfx) return;

        cbg.clearRect(0, 0, this.w, this.h);
        cs.clearRect(0, 0, this.w, this.h);
        cfx.clearRect(0, 0, this.w, this.h);

        this.drawBackground(cbg);
        this.drawScene(cs);
    }

    drawBackground(ctx) {
        const w = this.weather;
        const t = this.time;
        let grad;

        if (w === 'sunny') {
            const sunX = this.w * (1 - this.mouse.x * 0.3);
            const sunY = this.h * 0.2 * (1 + this.mouse.y * 0.5);
            grad = ctx.createRadialGradient(sunX, sunY, 10, sunX, sunY, this.w * 0.8);
            grad.addColorStop(0, '#ffd700');
            grad.addColorStop(0.2, '#ffaa00');
            grad.addColorStop(1, '#4da6ff');
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, this.w, this.h);

            // God rays
            ctx.save();
            ctx.translate(sunX, sunY);
            ctx.rotate(t * 0.05);
            ctx.fillStyle = 'rgba(255,255,200, 0.05)';
            for (let i = 0; i < 8; i++) {
                ctx.rotate(Math.PI / 4);
                ctx.beginPath();
                ctx.moveTo(0, 0);
                ctx.lineTo(this.w, -100);
                ctx.lineTo(this.w, 100);
                ctx.fill();
            }
            ctx.restore();
        } else if (w === 'rainy') {
            grad = ctx.createLinearGradient(0, 0, 0, this.h);
            grad.addColorStop(0, '#1a1a2e');
            grad.addColorStop(1, '#16213e');
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, this.w, this.h);

            // Clouds
            ctx.fillStyle = 'rgba(10,10,20, 0.3)';
            // Low-res rendering for performance
            for (let x = 0; x < this.w; x += 20) {
                for (let y = 0; y < this.h / 2; y += 20) {
                    const n = noise2D(x * 0.005 + t * 0.1, y * 0.01);
                    if (n > 0.3) ctx.fillRect(x, y, 20, 20);
                }
            }
        } else if (w === 'snowy') {
            grad = ctx.createLinearGradient(0, 0, 0, this.h);
            grad.addColorStop(0, '#a8c0ff');
            grad.addColorStop(1, '#3f2b96');
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, this.w, this.h);
        } else if (w === 'windy') {
            grad = ctx.createLinearGradient(0, 0, 0, this.h);
            grad.addColorStop(0, '#616161');
            grad.addColorStop(1, '#9bc5c3');
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, this.w, this.h);
        }

        if (this.lightning > 0.01) {
            ctx.fillStyle = `rgba(255,255,255,${this.lightning})`;
            ctx.fillRect(0, 0, this.w, this.h);
        }
    }

    drawScene(ctx) {
        const w = this.weather;
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = 'rgba(255,255,255,0.5)';

        this.particles.forEach(p => {
            if (w === 'rainy') {
                ctx.lineWidth = 1.5;
                ctx.globalAlpha = p.a;
                ctx.beginPath();
                ctx.moveTo(p.x, p.y);
                ctx.lineTo(p.x + (this.mouse.x - 0.5) * 5, p.y + p.l);
                ctx.stroke();
            } else if (w === 'snowy') {
                ctx.globalAlpha = rnd(0.5, 0.9);
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fill();
            } else if (w === 'windy') {
                ctx.save();
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rot);
                ctx.fillStyle = 'rgba(30,30,30,0.5)';
                ctx.fillRect(-p.r, -p.r / 2, p.r * 2, p.r);
                ctx.restore();
            } else if (w === 'sunny') {
                ctx.globalAlpha = p.a;
                ctx.beginPath();
                ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
                ctx.fill();
            }
        });
        ctx.globalAlpha = 1;
    }
}
